import { useState, useEffect, useRef, useMemo } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useGetMatterV2 } from '../../../api/queries/useGetMatterV2';
import { useGetSubset } from '../../../api/queries/useGetSubset';
import { RelevanceOptions } from '../../../constants/filterOptions';
import { CHIP_COLOURS } from '../../lib/chip-colours';
import { cn } from '../../lib/utils';
import { Chip } from '../chip';
import { FactDocViewer } from '../doc-viewer/fact-doc-viewer';
import { CommentSection } from '../fact/comments-section';
import { Icon } from '../icon';
import { ProcessingChip, ProcessingState } from '../processing-chip';
import { Button } from '../shadcn/button';
import { Skeleton } from '../shadcn/skeleton';
import { TooltipIcon } from '../tooltip-icon';
import { useGetFactV2 } from '@/api/queries/useGetFactV2';
import { Drawer } from '@/new/components/drawers/drawer';

interface FactWithDocumentDrawerProps {
  factId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export function FactWithDocumentDrawer({ factId, isOpen, onClose }: FactWithDocumentDrawerProps) {
  const { matterId, subsetId } = useParams();
  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);

  const requestSubsetId = subsetId ?? matter?.allDocumentsCaseId ?? undefined;

  const { data: fact, isLoading, error } = useGetFactV2(matterId, requestSubsetId, factId);
  const [isFactInfoExpanded, setIsFactInfoExpanded] = useState(false);
  const [isInfoPanelCollapsed, setIsInfoPanelCollapsed] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sourcesScrollRef = useRef<HTMLDivElement>(null);
  const [isSourcesScrolledToBottom, setIsSourcesScrolledToBottom] = useState(false);

  const selectedLocation = useMemo(
    () => fact?.locations.find((l) => l.docId === selectedDocId),
    [fact?.locations, selectedDocId],
  );

  const sortedSources = useMemo(
    () => selectedLocation?.sources?.toSorted((a, b) => parseInt(a.pages) - parseInt(b.pages) || 0),
    [selectedLocation?.sources],
  );

  // Check if sources are scrolled to bottom
  useEffect(() => {
    const target = sourcesScrollRef.current;
    if (target) {
      setIsSourcesScrolledToBottom(target.scrollHeight - target.scrollTop <= target.clientHeight + 1);
    }
  }, [sortedSources]);

  const handleSourcesScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    setIsSourcesScrolledToBottom(target.scrollHeight - target.scrollTop <= target.clientHeight + 1);
  };

  const handleClose = () => {
    setIsFactInfoExpanded(false);
    setIsInfoPanelCollapsed(false);
    setSelectedDocId(null);
    onClose();
  };

  // When a fact is loaded, automatically select the first document
  useEffect(() => {
    if (fact && fact.locations && fact.locations.length > 0) {
      const firstLocation = fact.locations[0];
      setSelectedDocId(firstLocation.docId);
    }
  }, [fact]);

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} hideCloseButton={true}>
      <div className="flex h-full flex-col">
        {error ? (
          <div className="my-10 flex h-full w-full flex-col items-center justify-center">
            <Icon className="lucide--triangle-alert text-lg text-amber-500" />
            <div className="mt-4 font-semibold">Sorry, there was a problem loading this fact.</div>
            <div className="text-xs text-gray-500">Please try again or contact support if the issue persists.</div>
          </div>
        ) : (
          <>
            {isLoading && (
              <div className="my-20 flex h-full w-full items-center justify-center">
                <Icon className="lucide--loader-2 animate-spin duration-700" />
              </div>
            )}
            {fact && (
              <>
                {/* Document Viewer Section */}
                <div className="-mb-8 flex-1 overflow-hidden">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={selectedDocId}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="h-full"
                    >
                      <FactDocViewer
                        docId={selectedDocId ?? ''}
                        factId={factId}
                        hideFeatureButtons={false}
                        hideCloseButton={true}
                        onViewerClick={() => setIsInfoPanelCollapsed(true)}
                      />
                    </motion.div>
                  </AnimatePresence>
                </div>

                <div className="z-10 max-h-9/10 overflow-hidden" onMouseEnter={() => setIsInfoPanelCollapsed(false)}>
                  <div className="mx-4 flex gap-1 overflow-x-scroll text-xs [scrollbar-width:none]">
                    {fact.locations.map((l) => (
                      <button
                        className={cn(
                          'rounded-t-lg border p-1 px-2.25 pt-1.5 text-xs font-medium duration-150',
                          l.doc.id === selectedDocId
                            ? 'z-30 border-b-white bg-white text-nowrap text-gray-600'
                            : 'truncate border-b-gray-200 bg-gray-50 text-gray-500 hover:bg-white',
                        )}
                        onClick={() => setSelectedDocId(l.docId)}
                      >
                        {l.doc.file.name}
                      </button>
                    ))}
                  </div>

                  <motion.div
                    className="relative z-20 -mt-px overflow-hidden border-t bg-white text-xs shadow-[0_-1px_8px_rgba(0,0,0,0.06)]"
                    animate={{ height: isInfoPanelCollapsed ? '16px' : 'auto' }}
                    transition={{ duration: 0.2 }}
                  >
                    <div
                      ref={scrollContainerRef}
                      className={cn(
                        isFactInfoExpanded ? 'overflow-y-auto' : 'overflow-hidden',
                        'max-h-[calc(90vh-3rem)] px-5',
                      )}
                    >
                      <p className={cn('mt-4 mb-2 flex items-center gap-2 font-semibold break-all text-gray-700')}>
                        <Icon className="lucide--calendar" />
                        <span className="-mb-0.25 line-clamp-1">{fact.factDateText ?? '(Date Unknown)'}</span>
                      </p>

                      <p className={`mb-3 text-gray-700`}>{fact.description}</p>

                      <div className="my-2 flex items-center gap-1.5">
                        <p className="font-medium">Source</p>

                        <Button
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              selectedLocation?.sources?.map((source) => source.text).join('\n\n') ?? '',
                            );
                            toast.success('Source text copied to clipboard');
                          }}
                          className="ml-1 px-1.5 py-0.75"
                        >
                          <Icon className="lucide--copy" />
                          <span className="text-[11px]">Copy</span>
                        </Button>

                        <Chip className={CHIP_COLOURS.type}>{selectedLocation?.doc?.type}</Chip>
                      </div>

                      <div
                        ref={sourcesScrollRef}
                        onScroll={handleSourcesScroll}
                        className="flex max-h-[30vh] flex-col gap-1.5 overflow-y-auto mask-b-from-70% mask-b-to-105% text-gray-600 transition-[mask-size]"
                        style={{ maskSize: isSourcesScrolledToBottom ? '100% 130%' : '100% 100%' }}
                      >
                        {sortedSources?.map((s, i) => (
                          <div key={i} className="text-xs">
                            <span className="font-medium text-gray-700">
                              [Page{s.pages.includes(',') ? 's' : ''} {s.pages}]
                            </span>{' '}
                            {s.text && s.text.length > 900 ? s.text.slice(0, 900) + '...' : s.text}
                          </div>
                        ))}
                      </div>

                      <div className="pt-1.5 pb-2">
                        <button
                          className="flex w-full justify-center p-1 text-gray-400 duration-150 hover:text-gray-900"
                          onClick={() => {
                            if (isFactInfoExpanded) {
                              scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                            setIsFactInfoExpanded(!isFactInfoExpanded);
                          }}
                        >
                          <Icon
                            className={cn(
                              isFactInfoExpanded ? 'lucide--chevron-down' : 'lucide--more-horizontal',
                              'text-base duration-300',
                            )}
                          />
                        </button>
                      </div>

                      <motion.div
                        animate={{ height: isFactInfoExpanded ? 'auto' : 0 }}
                        transition={{ duration: 0.2 }}
                        initial={false}
                      >
                        <div className="pb-5">
                          <div
                            className={cn(
                              'relative flex flex-col gap-3 rounded-md border bg-gray-50 px-4 py-3 text-xs',
                              !subset?.isAllDocuments && 'pt-9',
                            )}
                          >
                            {/* subset indicator */}
                            {!subset?.isAllDocuments && (
                              <div className="absolute -top-[1px] left-3 flex items-center gap-1.5 rounded-b-lg border bg-white px-2 py-1 text-xs font-medium text-gray-700">
                                <Icon className="lucide--group" />
                                <span>{subset?.name ?? ''}</span>
                              </div>
                            )}

                            {/* significance */}
                            <div className="flex grow flex-col gap-2">
                              <p className="flex items-center gap-2 font-medium">
                                <span>{subset?.isAllDocuments ? '' : 'Subset'} Significance</span>

                                <TooltipIcon
                                  side="right"
                                  tooltipContent={`Short analysis of this fact's significance as it pertains to this ${subset?.isAllDocuments ? `matter's` : `subset's`} issues`}
                                />
                              </p>

                              {fact.significance ? (
                                <p className="text-gray-700">{fact.significance}</p>
                              ) : (
                                <div className="flex flex-col gap-1.5">
                                  <Skeleton className="h-4 w-9/10 bg-gray-200" />
                                  <Skeleton className="h-4 w-2/10 bg-gray-200" />
                                </div>
                              )}
                            </div>

                            <div className="flex flex-col gap-2">
                              <p className="flex items-center gap-2 font-medium whitespace-nowrap">
                                {subset?.isAllDocuments ? '' : 'Subset'} Relevance
                                <TooltipIcon
                                  side="right"
                                  tooltipContent={`Is this fact relevant to the ${subset?.isAllDocuments ? 'matter' : 'subset'} issues?`}
                                />
                              </p>

                              {fact.relevance ? (
                                <p>{RelevanceOptions[fact.relevance].label}</p>
                              ) : (
                                <ProcessingChip
                                  processingState={ProcessingState.Processing}
                                  processingText="Processing Relevance"
                                />
                              )}
                            </div>
                          </div>

                          <div className="mx-1 mt-4">
                            <CommentSection fact={fact} />
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}
